import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Divider, Drawer, Skeleton } from '@mui/material';
import Masonry from '@mui/lab/Masonry';
import Breadcrumbs from '@brainpool/components/molecules/Breadcrumbs/Breadcrumbs';
import { reactiveVars } from '@brainpool/apollo-connect';

import PageContainer from '../../components/PageContainer/PageContainer';
import { HeaderAction } from '../../components/HeaderAction/HeaderAction';
import { OverviewHeader } from '../../components/OverviewHeader/OverviewHeader';
import { useGetCase } from '../../hooks/useGetCase';
import AdvisorMode from '../../components/AdvisorMode/AdvisorMode';
import OverviewWidget, { allowedWidgetIds } from '../../components/OverviewWidget/OverviewWidget';
import { ModalProps } from '../../types/ModalTypes';
import { useGetUser } from '../../hooks/useGetUser';
import { Widget } from '../../types/Widget';
import { WorkflowStatus } from '../../types/WorkflowState';

export function ProjectOverview() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const rawId = searchParams.get('id');
  const caseId = rawId ? `Case_${rawId}` : null;
  // get data from hooks
  const {
    data: caseData,
    loading,
    refetch: refetchCase,
  } = useGetCase({
    id: caseId || undefined,
    useCache: true,
  });
  const { getUser, data: userData } = useGetUser({ useCache: true });
  //state
  const [open, setOpen] = useState(false);

  useEffect(() => {
    getUser();
    if (caseId) {
      getUser();
      refetchCase({ input: caseId });
    }
  }, [caseId]);

  if (!caseId) {
    return null;
  }

  const handleRefresh = () => {
    if (caseId) {
      refetchCase({ id: caseId });
    }
  };

  //get data from cache
  const { id, role, enrichments, details, workflow_state, widgets, report_path } =
    caseData?.getCase || {};
  const { getUser: user } = userData || {};
  const { name } = details ?? {};
  const { status, risks, data_availability } = workflow_state ?? {};
  const { survey_responses } = user || {};

  //prepare widgets
  const risksCount =
    risks && typeof risks === 'object'
      ? (risks.standard?.length || 0) + (risks.critical?.length || 0)
      : null;
  const widgetOrder = [
    'widget_overviewoutput',
    'widget_fitscoreoutput',
    'widget_sectoroutput',
    'widget_teamoutput',
    'widget_uspoutput',
    'widget_techoutput',
    'widget_keyrisksoutput',
    'widget_widgetquestionsoutput',
  ];
  const widgetsList = (() => {
    const widgetsArray: Widget[] = Array.isArray(widgets) ? (widgets as Widget[]) : [];
    const widgetsCopy = widgetsArray.filter(
      (widget: Widget) => widget.id?.toLowerCase() !== 'widget_keyrisksoutput'
    );
    const keyRisksWidget = widgetsArray.find(
      (widget: Widget) => widget.id?.toLowerCase() === 'widget_keyrisksoutput'
    );
    if (keyRisksWidget && typeof keyRisksWidget.data === 'string') {
      const dataObj = JSON.parse(keyRisksWidget.data);
      if (dataObj.top_risks) {
        widgetsCopy.push({
          ...keyRisksWidget,
          id: 'widget_keyrisksoutput',
          data: { top_risks: dataObj.top_risks },
        });
      }
      if (dataObj.questions) {
        widgetsCopy.push({
          ...keyRisksWidget,
          id: 'widget_widgetquestionsoutput',
          data: { questions: dataObj.questions },
        });
      }
    }
    return widgetsCopy;
  })();
  // sort by widgetOrder (case-insensitive)
  const orderedWidgets = [...widgetsList].sort((a, b) => {
    const aIndex = widgetOrder.findIndex(orderId => orderId.toLowerCase() === a.id?.toLowerCase());
    const bIndex = widgetOrder.findIndex(orderId => orderId.toLowerCase() === b.id?.toLowerCase());
    return aIndex - bIndex;
  });

  const handleDrawer = () => {
    setOpen(prev => !prev);
  };

  // modal logic
  const handleOpenModal = (type: ModalProps['type']) => {
    reactiveVars.modalData({
      data: [{ role, id, details: { name } }],
      type,
      open: true,
      onClose: () => ({ open: false }),
      redirect: type === 'delete' ? true : false,
    });
  };

  return (
    <>
      <PageContainer testid="projects-overview-page">
        <Breadcrumbs
          breadcrumbs={[
            {
              label: 'Pipeline',
              url: '/projects',
            },
            {
              label: name || '',
            },
          ]}
        />
        <HeaderAction
          onOpenModal={handleOpenModal}
          onRefresh={handleRefresh}
          onAdvisorMode={handleDrawer}
          id={caseId}
          report_path={report_path}
          name={name || ''}
          status={status || WorkflowStatus.PENDING}
          role={role || 'Reader'}
        />

        <Divider sx={{ mb: 8 }} />
        <OverviewHeader dataReadiness={data_availability?.combined || 0} name={name} />

        <Masonry columns={{ xs: 1, sm: 2, md: 3 }} spacing={2} sx={{ my: 4 }}>
          {loading ? (
            <>
              {allowedWidgetIds.map(widgetId => (
                <Skeleton
                  key={widgetId}
                  variant="rounded"
                  sx={{ height: '300px', width: '100%', marginBottom: '1rem' }}
                />
              ))}
            </>
          ) : (
            <>
              {orderedWidgets.map(widget => (
                <OverviewWidget
                  enrichments={enrichments}
                  widget={widget as Widget}
                  loading={loading}
                  key={widget.id}
                  role={role}
                  risks={risksCount ?? undefined}
                  survey_responses={survey_responses}
                />
              ))}
            </>
          )}
        </Masonry>
      </PageContainer>

      {role && (
        <Drawer anchor="right" open={open} onClose={handleDrawer}>
          <AdvisorMode onClose={handleDrawer} caseId={id} userRole={role} />
        </Drawer>
      )}
    </>
  );
}

export default ProjectOverview;
